import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../../context/AppContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { capitalize, useMediaQuery } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import 'dayjs/locale/en-gb';
import axios from 'axios';
import './AddExamModal.css';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay';
import TourButton from '../../TourButton/TourButton';
import { getAddExamModalSteps } from '../../../tours/addExamModal';
import { useNotification } from '../../notifications/useNotification';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Renders the modal to create a new exam.
 *
 * @param {Object}   props
 * @param {Function} props.setShowModal - Callback to show/hide the modal.
 * @param {Function} props.onSubmit     - Async callback invoked after a successful save.
 * @param {Object}   props.client       - The client object; must contain `_id`.
 */
const AddExamModal = ({ setShowModal, onSubmit, client }) => {
    const { t } = useTranslation();
    const tourSteps = useMemo(() => getAddExamModalSteps(t), [t]);
    const { currentLanguage, dietist } = useAppContext();
    const { showError } = useNotification();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [date, setDate] = useState(dayjs(''));
    const [description, setDescription] = useState('');
    const [examType, setExamType] = useState('');

    const adapterLocale = currentLanguage === 'it' ? 'it' : 'en-gb';
    const calendarTheme = createTheme({
        palette: { primary: { main: '#fd4701', light: '#fc9f5b', dark: '#830102' } },
    });

    const handleDateChange = (newDate) => {
        setDate(newDate);
        setErrors(prev => { const { date, ...rest } = prev; return rest; });
    };

    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            if (file.size > MAX_FILE_SIZE_BYTES) {
                setErrors(prev => ({ ...prev, selectedFile: t('errorMessages.file-too-large', { mb: MAX_FILE_SIZE_MB }) }));
                return;
            }
            setSelectedFile(file);
        }
    };
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > MAX_FILE_SIZE_BYTES) {
                setErrors(prev => ({ ...prev, selectedFile: t('errorMessages.file-too-large', { mb: MAX_FILE_SIZE_MB }) }));
                return;
            }
            setSelectedFile(file);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!date || !date.isValid())
            newErrors.date = t('errorMessages.dateRequired');
        if (!examType.trim())
            newErrors.examType = t('errorMessages.field-required');
        if (!selectedFile)
            newErrors.selectedFile = t('errorMessages.file-required');
        else if (selectedFile.size > MAX_FILE_SIZE_BYTES)
            newErrors.selectedFile = t('errorMessages.file-too-large', { mb: MAX_FILE_SIZE_MB });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            try {
                setLoading(true);
                const fd = new FormData();
                fd.append('clientId', client._id);
                fd.append('dietistId', dietist._id);
                fd.append('examType', examType);
                fd.append('date', date);
                fd.append('description', description);
                fd.append('file', selectedFile);
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/exams`, fd, {
                    withCredentials: true,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                await onSubmit();
                handleClose();
            } catch (error) {
                console.error('Add exam modal error:', error);
                showError(t('unknown-error'));
            } finally {
                setLoading(false);
            }
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setIsDragging(false);
        setDescription('');
        setExamType('');
        setErrors({});
        setDate(dayjs(''));
        setShowModal(false);
    };

    return (
        <>
            <LoadingOverlay open={loading} />
            <div className="aem-overlay">
                <div className="aem-modal" data-tour="aem-modal">

                    {/* ── Header ── */}
                    <div className="aem-header">
                        <h2 className="aem-title">{capitalize(t('add-new-exam'))}</h2>
                        <div className='aem-tutorial-btn'>
                            <TourButton steps={tourSteps} iconOnly={isMobile} label={t('tour.tutorial-btn')} />
                        </div>
                        <button className="aem-close-btn" onClick={handleClose} aria-label="Close">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    {/* ── Body ── */}
                    <div className="aem-body">

                        {/* Date */}
                        <div data-tour="aem-date">
                        <ThemeProvider theme={calendarTheme}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                                <DatePicker
                                    label={t('Date')}
                                    value={date}
                                    onChange={handleDateChange}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            error: !!errors.date,
                                            helperText: errors.date,
                                            sx: {
                                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#fc9f5b',
                                                },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </ThemeProvider>
                        </div>

                        {/* Exam type */}
                        <div data-tour="aem-type">
                        <div className="aem-field">
                            <label className="aem-label">{t('exam-type')}</label>
                            <input
                                className={`aem-input${errors.examType ? ' aem-input-error' : ''}`}
                                type="text"
                                value={examType}
                                onChange={e => setExamType(e.target.value)}
                                placeholder={t('exam-type')}
                            />
                            {errors.examType && <span className="aem-error">{errors.examType}</span>}
                        </div>

                        {/* Description */}
                        <div className="aem-field">
                            <label className="aem-label">{capitalize(t('description'))}</label>
                            <textarea
                                className="aem-textarea"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder={t('enter-description')}
                                rows={3}
                            />
                        </div>
                        </div>

                        {/* Upload zone */}
                        <div
                            className={`aem-upload-zone${isDragging ? ' dragging' : ''}${errors.selectedFile ? ' error' : ''}`}
                            data-tour="aem-upload"
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('aem-file-input').click()}
                        >
                            <UploadFileOutlinedIcon className="aem-upload-icon" />
                            {!selectedFile ? (
                                <p className="aem-upload-text">{t('drag-drop-or-click-upload')}</p>
                            ) : (
                                <p className="aem-upload-filename">{selectedFile.name}</p>
                            )}
                            <input
                                id="aem-file-input"
                                type="file"
                                accept="image/*,application/pdf"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                        </div>
                        {errors.selectedFile && <span className="aem-error">{errors.selectedFile}</span>}

                        {/* File preview */}
                        {selectedFile && (
                            <div className="aem-preview">
                                {selectedFile.type === 'application/pdf' ? (
                                    <iframe
                                        src={URL.createObjectURL(selectedFile)}
                                        title="PDF preview"
                                        width="100%"
                                        height="400px"
                                        style={{ border: 'none' }}
                                    />
                                ) : (
                                    <img
                                        src={URL.createObjectURL(selectedFile)}
                                        alt="preview"
                                        className="aem-preview-img"
                                    />
                                )}
                                <button
                                    type="button"
                                    className="aem-remove-file-btn"
                                    onClick={() => setSelectedFile(null)}
                                >
                                    {t('remove')}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── Footer ── */}
                    <div className="aem-footer" data-tour="aem-footer">
                        <button className="aem-btn aem-btn-cancel" onClick={handleClose}>{t('Cancel')}</button>
                        <button className="aem-btn aem-btn-save" onClick={handleSubmit}>{t('Save')}</button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddExamModal;
